import React from 'react';
import { AgnesDataTable, type AgnesDataTableColumn, AgnesPageHeader, AgnesSwitch } from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { ChevronDown, ChevronUp, Download, LineChart, Users } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import CessaoFilter from './CessaoFilter';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import { type LinhaCessao, useCessaoStore } from '@/stores/useCessaoStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { countFiltros } from '@/lib/filtros';
import type { ChartSeries } from '@/lib/cessao';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

// cores das 2 séries (ônus cedente / cessionário), paridade com o legado.
const SERIE_CORES = ['#3B5BDB', '#E4C23F'];

// gráfico de barras agrupadas por período (view-model vem do BFF já formatado).
function GraficoRemuneracao({
  categories,
  series,
}: {
  categories: string[];
  series: ChartSeries[];
}) {
  const max = Math.max(1, ...series.flatMap((s) => s.data));
  return (
    <div className="p-4">
      <div className="flex items-end gap-4 overflow-x-auto pb-2">
        {categories.map((cat, i) => (
          <div key={cat} className="flex min-w-[64px] flex-col items-center gap-2">
            <div className="flex h-40 items-end gap-1">
              {series.map((s, j) => (
                <div
                  key={s.name}
                  className="w-4 rounded-t"
                  style={{
                    height: `${((s.data[i] ?? 0) / max) * 100}%`,
                    background: SERIE_CORES[j % SERIE_CORES.length],
                  }}
                  title={`${s.name}: ${s.data[i] ?? 0}`}
                />
              ))}
            </div>
            <span className="whitespace-nowrap text-xs text-agnes-text-muted">{cat}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {series.map((s, j) => (
          <span key={s.name} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: SERIE_CORES[j % SERIE_CORES.length] }}
            />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function PageCessao() {
  const {
    vm,
    lista,
    total,
    page,
    size,
    sortBy,
    sortOrder,
    busca,
    filtros,
    opcoes,
    contagem,
    cedidosNaDataCorrente,
    carregandoLista,
    exportando,
    carregarDashboard,
    carregarContagem,
    carregarHistorico,
    carregarFiltros,
    aplicarFiltros,
    setToggleCedidosNaDataCorrente,
    setBusca,
    buscar,
    setPage,
    setSize,
    setSort,
    exportar,
  } = useCessaoStore();

  const [showCharts, setShowCharts] = React.useState(false);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('egov-cessao');

  React.useEffect(() => {
    carregarFiltros();
    carregarDashboard();
    carregarContagem();
    carregarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cnpjCell = (empresa: string, cnpj: string | null) => (
    <div className="leading-tight">
      <p className="text-agnes-text">{empresa || 'Sem informação'}</p>
      <p className="text-agnes-text-muted">{cnpj || 'Sem informação'}</p>
    </div>
  );

  const columns: AgnesDataTableColumn<LinhaCessao>[] = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    {
      key: 'origem',
      header: 'Origem',
      width: 260,
      sortable: true,
      render: (r) => cnpjCell(r.empresaOrigem, r.cnpjOrigem),
    },
    {
      key: 'destino',
      header: 'Destino',
      width: 260,
      sortable: true,
      render: (r) => cnpjCell(r.empresaDestino ?? '', r.cnpjDestino),
    },
    { key: 'cpf', header: 'CPF', width: 140, sortable: true },
    {
      key: 'matricula',
      header: 'Matrícula',
      width: 150,
      sortable: true,
      render: (r) => (
        <span className="block max-w-[130px] truncate" title={r.matricula}>
          {r.matricula}
        </span>
      ),
    },
    {
      key: 'nome',
      header: 'Nome/Nome Social',
      width: 240,
      sortable: true,
      render: (r) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.nome}</p>
          {r.nomeSocial && <p className="text-agnes-text-muted">{r.nomeSocial}</p>}
        </div>
      ),
    },
    { key: 'tpRegPrev', header: 'Regime Previdenciário', width: 180, sortable: true },
    { key: 'situacao', header: 'Situação de cessão', width: 160, sortable: true },
    {
      key: 'inicio',
      header: 'Início',
      width: 130,
      sortable: true,
      render: (r) => <span>{r.inicio ?? 'Sem informação'}</span>,
    },
    {
      key: 'fim',
      header: 'Fim',
      width: 130,
      sortable: true,
      render: (r) => <span>{r.fim ?? 'Sem informação'}</span>,
    },
    {
      key: 'remuneracao',
      header: 'Remuneração na origem',
      width: 240,
      sortable: true,
      render: (r) => <span>{r.remuneracao ?? 'Sem informação'}</span>,
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<Users />}
        title="Cessão"
        subtitle="Monitore as informações relativas aos eventos S-2231 - Cessão/Exercício em outro Órgão do eSocial"
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'eGov' }, { label: 'Cessão' }]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-wrap items-center justify-end gap-4">
          <AgnesSwitch
            label={`Cedidos na data corrente (${contagem.cededOnCurrentDate})`}
            labelPosition="right"
            checked={cedidosNaDataCorrente}
            onCheckedChange={setToggleCedidosNaDataCorrente}
          />
          <CessaoFilter
            opcoes={opcoes}
            onApply={aplicarFiltros}
            count={countFiltros(filtros, { dateRanges: [['startDate', 'endDate']] })}
          />
        </div>

        {/* card do gráfico colapsável */}
        <div className="mt-4 rounded-xl border border-agnes-border bg-agnes-bg-default shadow-agnes-subtle">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-agnes-primary">
              <LineChart className="h-5 w-5" />
              <span className="font-medium">
                Histórico de remuneração no órgão de origem por período
                {vm.isEmpty && (
                  <span className="ml-2 font-normal text-agnes-text-muted">
                    (Não há registros a serem exibidos)
                  </span>
                )}
              </span>
            </div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-agnes-primary"
              onClick={() => setShowCharts((s) => !s)}
            >
              {showCharts ? 'Ocultar gráficos' : 'Exibir gráficos'}
              {showCharts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {showCharts && (
            <div className="border-t border-agnes-border">
              {vm.isEmpty ? (
                <div className="p-8 text-center text-agnes-text-muted">
                  Não há registros a serem exibidos.
                </div>
              ) : (
                <GraficoRemuneracao categories={vm.categories} series={vm.series} />
              )}
            </div>
          )}
        </div>

        {/* busca + exportar */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="min-w-[320px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={buscar}
              placeholder="Pesquisar por nome, matrícula ou CPF"
            />
          </div>
          <ColumnsMenu
            data-qa="cessao-colunas"
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            onToggleColumn={toggleColumn}
          />
          <ExportButton
            variant="outlined"
            startIcon={<Download className="h-4 w-4" />}
            disabled={exportando}
            onClick={exportar}
          >
            Exportar
          </ExportButton>
        </div>

        {/* tabela */}
        {!carregandoLista && lista.length === 0 ? (
          <EmptyState className="mt-4" title="Nenhum resultado encontrado" />
        ) : (
          <TableWrapper className="mt-4">
            <AgnesDataTable<LinhaCessao>
              columns={columns}
              hiddenColumnKeys={hiddenColumnKeys}
              data={lista}
              rowKey={(r) => r._key}
              loading={carregandoLista}
              sort={{ sortBy, sortOrder, onSortChange: setSort }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: setPage,
                onPageSizeChange: setSize,
              }}
            />
          </TableWrapper>
        )}
      </div>
    </>
  );
}
